import { usePostHog } from 'posthog-js/react';

// Common group types used throughout the application
type GroupType = 'healthCategory' | 'checkoutType' | 'userType';

/**
 * TODO: further improvements can be done to make this more typed based on the event name.
 */
type CommonEventProperties = {
  // Checkout related
  paymentProcessor?: string;
  checkoutTotal?: number;
  selectedTreatments?: string[];
  orderId?: string;
  orderResponse?: any;

  // Form related
  formName?: string;
  healthCategory?: string;

  // User related
  email?: string;
  userType?: string;

  // Navigation related
  pathname?: string;

  // Groups
  $groups?: Record<GroupType, string>;

  // Allow any additional properties
  [key: string]: unknown;
};

// User identification properties
type UserIdentificationProperties = {
  email: string | null;
  userType: string | null;

  // Allow any additional properties
  [key: string]: unknown;
};

type EventCategory = 'checkout_flow' | 'patient_form' | 'login_flow' | 'register_flow' | 'navigation';

type EventObject = string; // FUTURE: We can make this typed.

/**
 * There are the core actions for an event.
 * This is the recommended list from PostHog - see https://posthog.com/docs/product-analytics/best-practices.
 */
type EventAction =
  | 'click'
  | 'submit'
  | 'create'
  | 'view'
  | 'add'
  | 'invite'
  | 'update'
  | 'delete'
  | 'remove'
  | 'start'
  | 'end'
  | 'cancel'
  | 'fail'
  | 'generate'
  | 'send'
  | 'success';

type EventName = `${EventCategory}:${EventObject}_${EventAction}`;

export type AnalyticsHook = {
  /**
   * Capture an event with optional properties
   * @param eventName - The name of the event to capture
   * @param properties - Optional properties to include with the event
   */
  capture: (eventName: EventName, properties?: CommonEventProperties) => void;

  /**
   * Identify a user
   * @param userId - The user ID to identify
   * @param properties - Optional user properties
   */
  identify: (userId: string, properties?: UserIdentificationProperties) => void;

  /**
   * Group a collection of events under a common meta property.
   * @param groupType - The meta type of group (e.g., 'userType' or 'healthCategory')
   * @param groupKey - The group key to identify the events within the meta group
   */
  group: (groupType: GroupType, groupKey: string) => void;

  /**
   * Reset the user session
   */
  reset: () => void;

  /**
   * Check if analytics is enabled
   */
  isEnabled: boolean;
};

// Check if we're in development mode without analytics enabled
const isEnabled = !import.meta.env.DEV || !!import.meta.env.VITE_DEV_ENABLE_ANALYTICS;

export const useInternalAnalytics = (): AnalyticsHook => {
  const posthog = usePostHog();

  const capture = (eventName: EventName, properties?: CommonEventProperties) => {
    if (!isEnabled) {
      return;
    }

    posthog?.capture(eventName, properties);
  };

  const identify = (userId: string, properties?: UserIdentificationProperties) => {
    if (!isEnabled) {
      return;
    }

    posthog?.identify(userId, properties);
  };

  const group = (groupType: GroupType, groupKey: string) => {
    if (!isEnabled) {
      return;
    }

    posthog?.group(groupType, groupKey);
  };

  const reset = () => {
    if (!isEnabled) {
      return;
    }

    posthog?.reset();
  };

  return {
    capture,
    identify,
    group,
    reset,
    isEnabled,
  };
};
